import { Slot } from "@radix-ui/react-slot";
import {
  useFloating,
  useInteractions,
  useClick,
  useDismiss,
  flip,
  size as floatingSize,
  Placement,
  offset,
  autoUpdate,
  FloatingPortal,
} from "@floating-ui/react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Button, { ButtonSize, ButtonVariants } from "../Button/Button";
import styles from "./Dropdown.module.scss";
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  IconComponentType,
} from "../Icon/Icon";
import OptionalPortal from "../util/OptionalPortal";
import cx from "classnames";
import Tooltip from "../Tooltip/Tooltip";
import DropdownItemContext from "./DropdownItemContext";

interface ButtonItemProps {
  id?: string;
  className?: string;
  IconComponent?: IconComponentType;
  tooltip?: string;
  label: React.ReactNode;
  customNode?: React.ReactNode;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  href?: never;
  disabled?: boolean;
  items?: never;
}

type LinkItemProps = {
  id?: string;
  className?: string;
  IconComponent?: IconComponentType;
  tooltip?: string;
  label: React.ReactNode;
  customNode?: React.ReactNode;
  href: string;
  onClick?: never;
  disabled?: boolean;
  items?: never;
};

type ParentItemProps = {
  id?: string;
  className?: string;
  IconComponent?: IconComponentType;
  tooltip?: string;
  label: React.ReactNode;
  customNode?: React.ReactNode;
  href?: never;
  onClick?: never;
  disabled?: boolean;
  items: Item[];
};

type Item = ButtonItemProps | LinkItemProps | ParentItemProps | React.ReactNode;

const isButtonItem = (item: Item): item is ButtonItemProps =>
  !!(item as ButtonItemProps).onClick;

const isLinkItem = (item: Item): item is LinkItemProps =>
  !!(item as LinkItemProps).href;

const isParentItem = (item: Item): item is ParentItemProps =>
  !!(item as ParentItemProps).items;

interface ItemGroup {
  items: Item[];
}

export type DropdownProps = {
  size?: ButtonSize;
  variant?: ButtonVariants;
  flat?: boolean;
  items: ItemGroup[];
  className?: string;
  classNames?: {
    button?: string;
    menu?: string;
  };
  withinPortal?: boolean;
  asChild?: boolean;
  placement?: Placement;
  matchWidth?: boolean;
  offsetAmount?: number;
  children: React.ReactNode;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  leftIcon?: React.ReactNode;
  loading?: boolean;
  disabled?: boolean;
  showChevron?: boolean;
};

function ButtonItem({
  IconComponent,
  label,
  onClick,
  disabled,
  tooltip,
  className,
}: ButtonItemProps) {
  const Content = useMemo(
    () => (
      <button
        className={cx(styles.menuItem, className, {
          [styles.menuItemDisabled]: disabled,
        })}
        onClick={onClick}
        type="button"
        disabled={disabled}
      >
        {IconComponent ? <IconComponent size="small" /> : null}
        {typeof label !== "string" ? (
          label
        ) : (
          <span className={styles.menuItemText}>{label}</span>
        )}
      </button>
    ),
    [IconComponent, className, disabled, label, onClick],
  );

  if (tooltip) {
    return <Tooltip content={tooltip}>{Content}</Tooltip>;
  }

  return Content;
}

function LinkItem({
  IconComponent,
  label,
  href,
  onClick,
  disabled,
  tooltip,
  className,
}: Omit<LinkItemProps, "onClick"> & { onClick: () => void }) {
  const content = useMemo(
    () =>
      disabled ? (
        <div
          className={cx(styles.menuItem, {
            [styles.menuItemDisabled]: disabled,
          })}
        >
          {IconComponent ? <IconComponent size="small" /> : null}
          {typeof label !== "string" ? (
            label
          ) : (
            <span className={styles.menuItemText}>{label}</span>
          )}
        </div>
      ) : (
        <Link
          className={cx(styles.menuItem, className)}
          to={href}
          onClick={onClick}
        >
          {IconComponent ? <IconComponent size="small" /> : null}
          {typeof label !== "string" ? (
            label
          ) : (
            <span className={styles.menuItemText}>{label}</span>
          )}
        </Link>
      ),
    [IconComponent, className, disabled, href, label, onClick],
  );

  return tooltip ? <Tooltip content={tooltip}>{content}</Tooltip> : content;
}

function ParentItem({
  IconComponent,
  label,
  items,
  disabled,
  tooltip,
  className,
}: ParentItemProps) {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: "right-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      flip(),
      floatingSize({
        apply({ elements, availableHeight }) {
          elements.floating.style.maxHeight = `${availableHeight - 16}px`;
        },
      }),
      // offset(offsetAmount),
    ],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  const content = useMemo(
    () =>
      disabled ? (
        <div
          className={cx(styles.menuItem, {
            [styles.menuItemDisabled]: disabled,
          })}
        >
          {IconComponent ? <IconComponent size="small" /> : null}
          {typeof label !== "string" ? (
            label
          ) : (
            <span className={styles.menuItemText}>{label}</span>
          )}
          <ChevronRight className={styles.chevronRight} size="small" />
        </div>
      ) : (
        <button
          className={cx(styles.menuItem, className)}
          ref={refs.setReference}
          {...getReferenceProps()}
        >
          {IconComponent ? <IconComponent size="small" /> : null}
          {typeof label !== "string" ? (
            label
          ) : (
            <span className={styles.menuItemText}>{label}</span>
          )}
          <ChevronRight className={styles.chevronRight} size="small" />

          {isOpen ? (
            <FloatingPortal>
              <div
                ref={refs.setFloating}
                className={styles.menu}
                style={floatingStyles}
                {...getFloatingProps({ onClick: (e) => e.stopPropagation() })}
              >
                <div className={styles.menuInner}>
                  <div className={styles.menuItemGroup}>
                    {items.map((item, index) => (
                      <DropdownItem
                        key={index}
                        item={item}
                        index={index}
                        setIsOpen={setIsOpen}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </FloatingPortal>
          ) : null}
        </button>
      ),
    [
      IconComponent,
      className,
      disabled,
      floatingStyles,
      getFloatingProps,
      getReferenceProps,
      isOpen,
      items,
      label,
      refs.setFloating,
      refs.setReference,
    ],
  );

  return (
    <DropdownItemContext.Provider
      value={{
        isOpen,
        setIsOpen: setIsOpen,
      }}
    >
      {tooltip ? <Tooltip content={tooltip}>{content}</Tooltip> : content}
    </DropdownItemContext.Provider>
  );
}

function DropdownItem({
  item,
  index,
  setIsOpen,
}: {
  item: Item;
  index: number;
  setIsOpen: (isOpen: boolean) => void;
}) {
  if (!isButtonItem(item) && !isLinkItem(item) && !isParentItem(item)) {
    return item;
  }

  const { label, id, IconComponent, disabled, tooltip, className } = item;

  if (isButtonItem(item)) {
    return (
      <ButtonItem
        tooltip={tooltip}
        key={(id ?? typeof label !== "string") ? index : label}
        className={className}
        IconComponent={IconComponent}
        label={label}
        disabled={disabled}
        onClick={(e) => {
          item.onClick(e);
          setIsOpen(false);
        }}
      />
    );
  }

  if (isLinkItem(item)) {
    return (
      <LinkItem
        tooltip={tooltip}
        key={(id ?? typeof label !== "string") ? index : label}
        className={className}
        href={item.href}
        IconComponent={IconComponent}
        label={label}
        disabled={disabled}
        onClick={() => setIsOpen(false)}
      />
    );
  }

  return (
    <ParentItem
      tooltip={tooltip}
      key={(id ?? typeof label !== "string") ? index : label}
      className={className}
      IconComponent={IconComponent}
      label={label}
      disabled={disabled}
      items={item.items}
    />
  );
}

function Dropdown({
  items,
  size = "medium",
  variant = "outline",
  flat,
  className,
  classNames,
  withinPortal,
  asChild,
  children,
  offsetAmount = 4,
  placement = "bottom-start",
  header,
  matchWidth,
  footer,
  leftIcon,
  disabled,
  loading,
  showChevron = true,
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      flip(),
      floatingSize({
        apply({ elements, availableHeight }) {
          elements.floating.style.maxHeight = `${availableHeight - 16}px`;
        },
      }),
      offset(offsetAmount),
    ],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  return (
    <>
      {asChild ? (
        <Slot ref={refs.setReference} {...getReferenceProps()}>
          {children}
        </Slot>
      ) : (
        <Button
          disabled={disabled}
          ref={refs.setReference}
          leftIcon={leftIcon}
          className={cx(className, classNames?.button)}
          size={size}
          flat={flat}
          loading={loading}
          rightIcon={
            showChevron ? (
              isOpen ? (
                <ChevronUp size={size === "xsmall" ? "small" : "medium"} />
              ) : (
                <ChevronDown size={size === "xsmall" ? "small" : "medium"} />
              )
            ) : undefined
          }
          variant={variant}
          {...getReferenceProps()}
        >
          {children}
        </Button>
      )}
      {isOpen ? (
        <OptionalPortal withinPortal={withinPortal}>
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              width: matchWidth
                ? refs.reference.current?.getBoundingClientRect().width
                : undefined,
            }}
            className={cx(styles.menu, classNames?.menu)}
            {...getFloatingProps()}
          >
            {header}
            <div className={styles.menuInner}>
              {items.map((itemGroup, index) => (
                <div key={index} className={styles.menuItemGroup}>
                  {itemGroup.items.map((item, index) => (
                    <DropdownItem
                      key={index}
                      item={item}
                      index={index}
                      setIsOpen={setIsOpen}
                    />
                  ))}
                </div>
              ))}
            </div>
            {footer}
          </div>
        </OptionalPortal>
      ) : null}
    </>
  );
}

export default Dropdown;
